import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Plus, Trash2, AlertTriangle, CheckCircle, Split } from 'lucide-react';

const formatCurrency = (v) => {
  const n = Number(v) || 0;
  return `R ${n < 0 ? '-' : ''}${Math.abs(n).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return d; }
};

/**
 * Split one uploaded transaction into N parts (cash / non-cash).
 *
 * Use case: a recorded entry represents both an actual bank payment AND a
 * non-cash transfer. Admin splits so the cash portion goes through the normal
 * bank-allocation flow. The non-cash portion also stays unmatched (2026-10-14)
 * so the admin can reconcile it against another uploaded transaction.
 */
export function SplitTransactionDialog({ open, onOpenChange, transaction, onConfirm, loading }) {
  const [parts, setParts] = useState([
    { amount: '', kind: 'cash', reference: '', notes: '' },
    { amount: '', kind: 'non_cash', reference: '', notes: '' },
  ]);

  const original = Number(transaction?.amount) || 0;
  const sign = original < 0 ? -1 : 1;

  useEffect(() => {
    // Start with two parts: leave the FIRST part empty for the admin to type,
    // and pre-fill the second with the residual so they only enter one number.
    setParts([
      { amount: '', kind: 'cash', reference: '', notes: '' },
      { amount: '', kind: 'non_cash', reference: '', notes: '' },
    ]);
  }, [transaction?.id]);

  const partsTotal = useMemo(
    () => parts.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0),
    [parts]
  );
  const diff = +(original - partsTotal).toFixed(2);
  // Unrounded, matching the server: amounts can carry a sub-cent.
  const balanced = Math.abs(original - partsTotal) <= 0.01 + 1e-6;

  const updatePart = (i, patch) => {
    setParts(prev => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  };

  const addPart = () => {
    setParts(prev => [...prev, { amount: '', kind: 'cash', reference: '', notes: '' }]);
  };

  const removePart = (i) => {
    setParts(prev => prev.length > 2 ? prev.filter((_, idx) => idx !== i) : prev);
  };

  const fillResidualInto = (i) => {
    const others = parts.reduce((s, p, idx) => idx === i ? s : s + (parseFloat(p.amount) || 0), 0);
    const rest = +(original - others).toFixed(2);
    updatePart(i, { amount: rest.toFixed(2) });
  };

  const canConfirm = balanced
    && parts.every(p => parseFloat(p.amount) !== 0 && p.amount !== '')
    && parts.length >= 2
    && !loading;

  const handleConfirm = () => {
    const payload = {
      parts: parts.map(p => ({
        amount: parseFloat(p.amount),
        kind: p.kind,
        reference: p.reference || null,
        notes: p.notes || null,
      })),
    };
    onConfirm(payload);
  };

  if (!transaction) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="split-transaction-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Split className="h-5 w-5 text-indigo-600" />
            Split Transaction
          </DialogTitle>
          <DialogDescription>
            Split this recorded transaction into multiple parts. Use <strong>Cash</strong> for portions that
            will be matched against bank transactions, and <strong>Non-cash</strong> for transfers that have
            no bank counterpart. All parts stay <strong>unmatched</strong> for you to reconcile, e.g. tick the
            non-cash part together with the uploaded transaction it offsets and Verify both. Parts must sum to the original amount.
          </DialogDescription>
        </DialogHeader>

        {/* Original Summary */}
        <div className="bg-slate-50 border rounded-lg p-4 grid grid-cols-2 gap-3" data-testid="split-original-summary">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide">Original Transaction</p>
            <p className="font-medium text-sm mt-1">{transaction.investor_name || transaction.investor_id}</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {formatDate(transaction.transaction_date)}
              {transaction.reference && <span className="ml-2">• {transaction.reference}</span>}
              {transaction.loan_id && <span className="ml-2 font-mono text-indigo-700">• {transaction.loan_id}</span>}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">Type: {(transaction.transaction_type || '').replace(/_/g, ' ')}</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-slate-800">{formatCurrency(original)}</p>
            <div className="text-xs mt-1">
              <span className="text-slate-500">Parts total: </span>
              <strong className={balanced ? 'text-green-700' : 'text-amber-700'}>{formatCurrency(partsTotal)}</strong>
            </div>
            <div className="text-xs">
              <span className="text-slate-500">Difference: </span>
              <strong className={balanced ? 'text-green-700' : 'text-red-700'}>{formatCurrency(diff)}</strong>
            </div>
          </div>
        </div>

        {/* Parts editor */}
        <div className="flex-1 overflow-y-auto space-y-2" data-testid="split-parts-editor">
          {parts.map((p, i) => (
            <div key={i} className="border rounded-lg p-3 bg-white space-y-2" data-testid={`split-part-${i}`}>
              <div className="flex items-center gap-2">
                <Badge className={p.kind === 'cash' ? 'bg-emerald-100 text-emerald-700' : 'bg-purple-100 text-purple-700'}>
                  Part {i + 1} · {p.kind === 'cash' ? 'Cash' : 'Non-cash'}
                </Badge>
                <select
                  value={p.kind}
                  onChange={e => updatePart(i, { kind: e.target.value })}
                  className="h-7 text-xs border rounded px-2"
                  data-testid={`split-part-kind-${i}`}
                >
                  <option value="cash">Cash (allocate against bank)</option>
                  <option value="non_cash">Non-cash (no bank match, stays unmatched)</option>
                </select>
                <div className="flex-1" />
                {parts.length > 2 && (
                  <button
                    onClick={() => removePart(i)}
                    className="text-red-400 hover:text-red-600"
                    title="Remove this part"
                    data-testid={`split-part-remove-${i}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number" step="0.01"
                  placeholder={`Amount (sign: ${sign > 0 ? '+' : '-'})`}
                  value={p.amount}
                  onChange={e => updatePart(i, { amount: e.target.value })}
                  className="h-8 w-44 text-sm font-mono"
                  data-testid={`split-part-amount-${i}`}
                />
                <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => fillResidualInto(i)} data-testid={`split-part-fill-residual-${i}`}>
                  Fill residual
                </Button>
                <Input
                  placeholder="Reference (optional — defaults to original/CASH-1 etc.)"
                  value={p.reference}
                  onChange={e => updatePart(i, { reference: e.target.value })}
                  className="h-8 flex-1 text-sm"
                  data-testid={`split-part-reference-${i}`}
                />
              </div>
              <Input
                placeholder="Notes (optional)"
                value={p.notes}
                onChange={e => updatePart(i, { notes: e.target.value })}
                className="h-8 text-sm"
                data-testid={`split-part-notes-${i}`}
              />
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addPart} className="text-xs" data-testid="split-add-part-btn">
            <Plus className="h-3 w-3 mr-1" /> Add another part
          </Button>
        </div>

        <DialogFooter className="flex items-center justify-between gap-2 border-t pt-4">
          <div className="flex items-center gap-2 text-xs">
            {!balanced ? (
              <Badge variant="destructive" className="text-xs">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Parts must sum to {formatCurrency(original)} (off by {formatCurrency(diff)})
              </Badge>
            ) : (
              <Badge className="bg-green-100 text-green-800 text-xs">
                <CheckCircle className="h-3 w-3 mr-1" />
                Balanced
              </Badge>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="split-cancel-btn">Cancel</Button>
            <Button
              onClick={handleConfirm}
              disabled={!canConfirm}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="split-confirm-btn"
            >
              {loading ? 'Splitting…' : `Split into ${parts.length} part(s)`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
